/**
 * Message fragments browsers use when a lazily loaded bundle cannot be fetched.
 *
 * Each engine words this differently, and the webpack runtime adds two of its own, so the
 * whole set has to be recognized:
 *
 * - `ChunkLoadError` / `Loading chunk` — webpack failed to fetch a lazy chunk.
 * - `Cannot find module` — webpack fetched a chunk whose module ids do not match the loaded
 *   runtime. Hashed filenames make this unreachable, but it is the error this handler was
 *   written for, so it stays covered.
 * - The remaining three are Chrome, Firefox and Safari's wording for a failed native
 *   `import()`, which is how Ionic loads its own component chunks.
 */
const CHUNK_LOAD_ERROR_PATTERNS = [
  'ChunkLoadError',
  'Loading chunk',
  'Loading CSS chunk',
  'Cannot find module',
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
];

/**
 * Determines whether an error was caused by a bundle that could not be loaded.
 *
 * @param error - The error to inspect. Any type is accepted, since these surface through
 * `ErrorHandler`, router `NavigationError` events and `unhandledrejection` alike.
 * @returns `true` when the error looks like a failed bundle fetch.
 *
 * @remarks
 * After a deploy the previous build's files are gone — Firebase Hosting releases a full
 * snapshot rather than layering versions — so a session that was open across the deploy
 * requests chunk filenames that no longer exist and 404s on the next lazy route.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) {
    return false;
  }

  if (error instanceof Error && error.name === 'ChunkLoadError') {
    return true;
  }

  let message: unknown = error;

  if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === 'object' && 'message' in error) {
    message = (error as { message: unknown }).message;
  }

  if (typeof message !== 'string') {
    return false;
  }

  // Narrowing on a `let` does not survive into the callback, so bind it first.
  const text = message;

  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => text.includes(pattern));
}
